import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../../auth/firebaseConfig';
import { MaintenanceRequest } from './MaintenanceRequestCard';
import LandlordMaintenanceView from './LandlordMaintenanceView';
import { Listing as Property } from '../../property/property-card/PropertyCardTypes';
import { queryDocuments } from '../../../utility/firebaseHelpers';
import { where, Timestamp } from 'firebase/firestore';

const LandlordMaintenanceContainer: React.FC = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<(string | null)[]>([null]);

  const filteredRequestsForLandlord = useMemo(() => {
    return requests.filter((req) => {
      const statusMatch = statusFilter === 'All' || req.status === statusFilter;
      const tenant = (req as any).tenantInfo;
      const property = properties.find((p) => p.id === Number(req.listingId));

      const searchTermLower = searchTerm.toLowerCase();
      const searchMatch =
        searchTerm === '' ||
        tenant?.name?.toLowerCase().includes(searchTermLower) ||
        tenant?.email?.toLowerCase().includes(searchTermLower) ||
        property?.title?.toLowerCase().includes(searchTermLower);

      return statusMatch && searchMatch;
    });
  }, [requests, statusFilter, searchTerm, properties]);

  const fetchRequests = useCallback(
    async (options?: { pageIndex?: number; cursor?: string | null }) => {
      if (!currentUser) return;
      setIsLoading(true);
      try {
        const baseUrl = `${CLOUD_FUNCTIONS_BASE_URL}/getMaintenanceRequestsForLandlord?landlordId=${currentUser.uid}`;
        const url = new URL(baseUrl);
        url.searchParams.set('pageSize', '10');

        const targetPage = options?.pageIndex ?? 1;
        const cursor = options?.cursor ?? null;

        if (cursor) url.searchParams.set('pageToken', cursor);

        const response = await fetch(url.toString());
        const data = await response.json();

        if (response.ok) {
          const parsedRequests = (data.requests || []).map((req: any) => ({
            ...req,
            createdAt: req.createdAt?._seconds
              ? new Timestamp(
                  req.createdAt._seconds,
                  req.createdAt._nanoseconds
                )
              : typeof req.createdAt === 'string'
                ? Timestamp.fromDate(new Date(req.createdAt))
                : req.createdAt,
            updatedAt: req.updatedAt?._seconds
              ? new Timestamp(
                  req.updatedAt._seconds,
                  req.updatedAt._nanoseconds
                )
              : typeof req.updatedAt === 'string'
                ? Timestamp.fromDate(new Date(req.updatedAt))
                : req.updatedAt
          }));
          setRequests(parsedRequests);
          setNextPageToken(data.nextPageToken || null);
          if (data.nextPageToken) {
            setPageCursors((prev) => {
              const newCursors = [...prev];
              if (!newCursors[targetPage]) {
                newCursors[targetPage] = data.nextPageToken;
              }
              return newCursors;
            });
          }
        } else {
          toast.error('Failed to fetch requests');
        }
      } catch (error) {
        toast.error('Error loading maintenance requests.');
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser]
  );

  const fetchLandlordProperties = useCallback(async () => {
    if (!currentUser) return;
    const landlordProperties = await queryDocuments<Property>('listings', [
      where('propertyOwnerUid', '==', currentUser.uid)
    ]);
    setProperties(landlordProperties);
  }, [currentUser]);

  useEffect(() => {
    setNextPageToken(null);
    setPage(1);
    setPageCursors([null]);
    fetchRequests({ pageIndex: 1, cursor: null });
    fetchLandlordProperties();
  }, [fetchRequests, fetchLandlordProperties]);

  const goToPage = async (targetPage: number) => {
    setIsLoadingMore(true);
    const cursor = pageCursors[targetPage - 1];
    await fetchRequests({ pageIndex: targetPage, cursor });
    setPage(targetPage);
    setIsLoadingMore(false);
  };

  const handleStatusChange = async (requestId: string, status: string) => {
    setIsUpdating(requestId);
    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/updateMaintenanceRequestStatus`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId, status })
        }
      );
      if (response.ok) {
        toast.success('Status updated');
        // Optimistic update or refetch
        setRequests((prev) =>
          prev.map((r) =>
            r.id === requestId
              ? { ...r, status: status as MaintenanceRequest['status'] }
              : r
          )
        );
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Error updating status.');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    setIsDeleting(requestId);
    try {
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/deleteMaintenanceRequest`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId })
        }
      );
      if (response.ok) {
        toast.success('Request deleted');
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
      } else {
        toast.error('Failed to delete request');
      }
    } catch (error) {
      toast.error('Error deleting request.');
    } finally {
      setIsDeleting(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-96 w-full items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <LandlordMaintenanceView
      isLoading={isLoading}
      requests={requests}
      filteredRequests={filteredRequestsForLandlord}
      handleStatusChange={handleStatusChange}
      handleDeleteRequest={handleDeleteRequest}
      isUpdating={isUpdating}
      isDeleting={isDeleting}
      properties={properties}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      page={page}
      hasNext={Boolean(nextPageToken)}
      onPageChange={goToPage}
      isLoadingPage={isLoadingMore}
    />
  );
};

export default LandlordMaintenanceContainer;
